import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import type { OpenGuideSource } from "@expert/solve-tools";
import {
    guidedFlowRecommendationMap,
    isGuideRecommendationDisabled,
    isGuideSupported,
    setRecommendation,
    useGuideStore,
} from "@expert/solve-tools";
import { addGuideRecommendationMessage } from "@soluto-private/expert-workspace-timeline";
import { useEffect } from "react";
import { getRootDispatcher } from "@/analytics";

export const useGuideRecommendation = ({
    callSid,
    isGuideOpen,
    logger,
    openGuide,
    sessionId,
}: {
    callSid?: string;
    isGuideOpen: boolean;
    logger: Logger;
    openGuide: (source: OpenGuideSource, guide?: string) => void;
    sessionId: string;
}) => {
    const rootDispatcher = getRootDispatcher();

    const enhancedDispatcher = callSid ? rootDispatcher.withIdentities({ CallSid: callSid }) : rootDispatcher;

    const {
        features: { isGuidedFlowRecommendationEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const { disableAutomaticOpen, recommendation } = useGuideStore();

    useEffect(() => {
        if (!isGuidedFlowRecommendationEnabled) return;
        if (!isGuideOpen && !disableAutomaticOpen && recommendation) {
            const guideToAutoOpen = guidedFlowRecommendationMap[recommendation];

            const shouldAutoOpen =
                guideToAutoOpen && isGuideSupported(guideToAutoOpen) && !isGuideRecommendationDisabled(guideToAutoOpen);

            if (shouldAutoOpen) {
                logger.info({ recommendation }, `Solve Guide | auto opening guide for recommendation`);
                void enhancedDispatcher.dispatchBusinessEvent("Guide_RecommendationMessageShown", {
                    guide: guideToAutoOpen,
                });
                trackFullStoryEvent("Guide_RecommendationMessageShown", { guide: guideToAutoOpen });
                addGuideRecommendationMessage(guideToAutoOpen, sessionId);
                openGuide({ source: "auto", sourceDetails: "recommendation" }, guideToAutoOpen);
            } else {
                logger.info(
                    {
                        isGuideSupported: isGuideSupported(guideToAutoOpen),
                        isGuideRecommendationDisabled: isGuideRecommendationDisabled(guideToAutoOpen),
                        recommendation,
                    },
                    `Solve Guide | not opening guide - either not supported or disabled`,
                );
            }
        } else if (recommendation) {
            logger.info(
                { disableAutomaticOpen, recommendation },
                `Solve Guide | recommendation received, but not opening guide`,
            );
        }
        setRecommendation(logger, undefined);
        // oxlint-disable-next-line exhaustive-deps
    }, [isGuideOpen, recommendation, setRecommendation]);
};
